import { Layout } from 'antd'
import { FC, useState } from 'react'
import { Redirect, Switch } from 'react-router'
import { Route } from 'react-router-dom'
import { Navigation } from '../components/antd_components/Navigation'
import BackgroundBox from '../components/custom_components/BackgroundBox'
import { ContentContainer } from '../components/custom_components/ContentContainer'
import PerFlexRow from '../components/custom_components/FlexRow'
import { useAuth } from '../context/auth-context'
import { RbacProvider } from '../context/rbac-context'
import { FeatureNameEnum } from '../graphql/featureToggle/FeatureNameEnum'
import useFeatureToggle from '../graphql/featureToggle/useFeatureToggle'
import EmployeePrepareViewContainer from '../modules/appraisal/appraisal-flow/employee-preparation/EmployeePrepareViewContainer'
import ManagerPrepareViewContainer from '../modules/appraisal/appraisal-flow/manager-preparation/ManagerPrepareViewContainer'
import { AppraisalMeetingContainer } from '../modules/appraisal/appraisal-flow/meeting/MeetingViewContainer'
import { AllCompaniesSettingsView } from '../modules/companySettings/AllCompanySettingsView'
import { CompanySettingsView } from '../modules/companySettings/CompanySettingsView'
import { CultivatingTalentsPage } from '../modules/cultivatingTalents/CultivatingTalentsPage'
import { EmployeesViewContainer } from '../modules/employees/EmployeesViewContainer'
import { EmployeeListPermissions } from '../modules/employees/permissions'
import { GlobalAdminView } from '../modules/globalAdmin/GlobalAdminView'
import { MeetingsPage } from '../modules/meeting/MeetingsPage'
import { OwnGrowthPage } from '../modules/ownGrowth/OwnGrowthPage'
import { PrivateProfilePage } from '../modules/profile/PrivateProfilePage'
import { PublicProfilePageContainer } from '../modules/profile/PublicProfilePageContainer'
import { PyramidsView } from '../modules/pyramids/PyramidsView'
import { ReportsContainer } from '../modules/reports/ReportsContainer'
import { SearchResultsPage } from '../modules/searchResults/SearchResultsPage'
import { featureEnabled } from '../shared/featureToggles/feature-toggle-helper'
import { hasPermission } from '../shared/permissions/permission-helper'
import { ErrorNotFound, ErrorUnknownUser } from './ErrorPages'
import { PermissionRoute } from './PermissionRoute'
import { Sidebar } from './Sidebar'
import { CollapseFromState } from './collapse-context'
import { authenticatedAppRoutes } from './routes'
import { HRSettingsView } from '../modules/companySettings/hrSettings/HRSettingsView'
import { ScoreGridsPage } from '../modules/scoreGrids/ScoreGridsPage'
import PerSpacer from '../components/custom_components/Spacer'
import { RoleName } from '../types'
import { HRSupportView } from '../modules/companySettings/hrSupport/HrSupportView'
import { KudosManagementPage } from '../modules/kudos/KudosManagementPage'
import { AttentionPointManagementPage } from '../modules/kudos/AttentionPointManagementPage'
import DashboardView from '../modules/dashboard/DashboardView'

const { Header, Content } = Layout

const AuthenticatedApp: FC = () => {
  const { user } = useAuth()
  const userRoles = user.roles || []
  const { data: featureTogglesData, loading, error } = useFeatureToggle()
  const [collapsed, setCollapsed] = useState<boolean>(false)

  const kudosEnabled =
    featureTogglesData &&
    featureTogglesData.featureToggles &&
    !loading &&
    !error &&
    featureEnabled(FeatureNameEnum.KUDOS, featureTogglesData.featureToggles)

  const dashboardEnabled =
    featureTogglesData &&
    featureTogglesData.featureToggles &&
    !loading &&
    !error &&
    featureEnabled(FeatureNameEnum.DASHBOARD, featureTogglesData.featureToggles)

  const getLandingPageByUserRoles = () => {
    const roles = userRoles ? userRoles : []
    const isCompanyManagerOrHRBP = roles.includes(RoleName.CompanyManager) || roles.includes(RoleName.BusinessPartner)
    const isBuManager = roles.includes(RoleName.BuManager)

    const isClusterOrNetworkManager = roles.includes(RoleName.ClusterManager) || roles.includes(RoleName.NetworkManager)
    return isCompanyManagerOrHRBP
      ? dashboardEnabled
        ? DashboardView
        : EmployeesViewContainer
      : isClusterOrNetworkManager
      ? CompanySettingsView
      : isBuManager
      ? EmployeesViewContainer
      : PrivateProfilePage
  }

  return (
    <RbacProvider>
      <CollapseFromState state={collapsed}>
        <Layout>
          <Header className="navigation-wrapper">
            <Navigation setCollapsed={setCollapsed} />
          </Header>
          <Layout className="per-content-layout">
            <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
            <Content>
              <Switch>
                <Route exact path="/" component={getLandingPageByUserRoles()} />
                <Route exact path="/unknown-user" component={ErrorUnknownUser} />

                <Redirect exact={false} from="/login-sso" to="/" />

                <Route exact path="/myProfile" component={PrivateProfilePage} />
                <Route exact path="/profile/:id/:resigned?" component={PublicProfilePageContainer} />
                <Route exact path="/employees" component={EmployeesViewContainer} />

                <Route exact path="/employeePrepareMeeting/:id" component={EmployeePrepareViewContainer} />
                <Route exact path="/managerPrepareMeeting/:id" component={ManagerPrepareViewContainer} />
                <Route exact path="/meetings/:id" component={AppraisalMeetingContainer} />
                <Route exact path="/meetings" component={MeetingsPage} />
                {kudosEnabled && <Route exact path="/kudos" component={KudosManagementPage} />}
                {kudosEnabled && <Route exact path="/attention-points" component={AttentionPointManagementPage} />}
                <Route exact path="/own-growth" component={OwnGrowthPage} />
                <Route exact path="/rankings" component={CultivatingTalentsPage} />
                <Route exact path="/score-grids" component={ScoreGridsPage} />
                <Route exact path="/pyramids" component={PyramidsView} />
                {/* <Route exact path="/notes" component={Notes} /> */}
                <Route exact path="/your-company" component={CompanySettingsView} />
                <Route exact path="/all-companies" component={AllCompaniesSettingsView} />
                <Route exact path="/hr-settings" component={HRSettingsView} />
                <Route exact path="/hr-support" component={HRSupportView} />
                {/* <Route exact path="/backoffice/:id" component={Backoffice} /> */}
                <Route exact path="/global-admin" component={GlobalAdminView} />

                <Route exact path="/downloads" component={ReportsContainer} />

                {dashboardEnabled && <Route exact path="/dashboard" component={DashboardView} />}

                <PermissionRoute
                  exact
                  path={authenticatedAppRoutes.searchResults()}
                  hasPermission={hasPermission(EmployeeListPermissions.employeeSearchResults, userRoles)}
                  featureEnabled={
                    featureTogglesData &&
                    featureTogglesData.featureToggles &&
                    !error &&
                    !loading &&
                    featureEnabled(FeatureNameEnum.GLOBAL_SEARCH, featureTogglesData.featureToggles)
                  }
                >
                  <SearchResultsPage />
                </PermissionRoute>
                <Route>
                  {!loading && (
                    <ContentContainer collapsed={collapsed}>
                      <BackgroundBox r color="white" className="fullWidth fullHeight">
                        <PerFlexRow align="middle" justify="center" className="fullHeight">
                          <PerSpacer mb="l">
                            <ErrorNotFound />
                          </PerSpacer>
                        </PerFlexRow>
                      </BackgroundBox>
                    </ContentContainer>
                  )}
                </Route>
              </Switch>
            </Content>
          </Layout>
        </Layout>
      </CollapseFromState>
    </RbacProvider>
  )
}

export { AuthenticatedApp }
